/* eslint-disable no-param-reassign */
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createStructuredSelector } from 'reselect';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import {
    productAnnouncementsFlagData,
    checkoutOrderReviewCTA as checkoutOrderReviewCTAFlag,
} from 'ggApp/modules/featureFlag';

import { ButtonV2 } from '@getgrover/ui';

import useMount from 'ggApp/shared/hooks/useMount';
import { profileNethone } from 'nethone';
import { TouchScrollable } from 'react-scrolllock-configurable';

import { registerAvailabilityOfProduct } from 'ggApp/modules/gaAnalytics/actions';
import { PureTranslator, Translator } from 'ggApp/modules/translator/components';
import If from 'ggApp/shared/components/Conditional';
import withDeviceInfo from 'ggApp/shared/components/viewport/withDeviceInfo';
import getProductAnnouncementData from 'ggApp/modules/product/utils/getProductAnnouncementData';

import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';
import { findAddressById } from 'ggApp/shared/context/Order/helpers/findAddressById';
import { CARD_TYPE } from 'ggApp/modules/newCheckout/components/InfoCard';
import {
    CHECKOUT_FLOW_NAMES as FLOW_NAMES,
    CHECKOUT_STEP_NAMES as STEP_NAMES,
} from 'ggApp/modules/newCheckout/constants';
import { OrderModes } from 'ggApp/shared/context/Order/types';
import { MixItemsContainer } from 'ggApp/shared/components/Cart/MixItemsContainer';
import { updateOrderStepState } from 'ggApp/shared/context/Order/services/tracking';

import {
    ButtonWrap,
    Card,
    Cards,
    ItemsContainer,
    CartItem,
    CircleLoader,
    Container,
    Header,
    MixItemsWrap,
    MobileImp,
    OrderInformation,
    Summary,
    SummaryNote,
    SummaryWrapper,
    UnavailableProduct,
    UserInformation,
    Voucher,
    WidgetContainer,
    ConsentNote,
} from './styles';

interface OrderReviewProps {
    userDevice?: string;
    checkoutAttemptReference?: string;
    closeCheckout: () => {};
    stepData: {
        heading?: string;
    };
    flowName?: string;
    nethone: {
        checkoutAttemptReference: string;
        isNethoneCrashed: boolean;
    };
    announcementFlagData?: any;
    checkoutOrderReviewCTA?: { variantKey?: string };
}

const UnavailableItemMemoized = React.memo(({ item, key, removeAllUnavailableProducts }) => {
    const { send } = useContext(OrderContext);

    const removeProduct = useCallback(() => send('REMOVE_LINE_ITEM', { item }), [item, send]);

    return (
        <UnavailableProduct
            item={item}
            key={key}
            onRemove={removeProduct}
            onSelectSimilarProduct={removeAllUnavailableProducts}
        />
    );
});

const OrderReview = ({
    stepData,
    userDevice,
    flowName,
    nethone = {},
    closeCheckout,
    announcementFlagData,
    checkoutOrderReviewCTA,
}: OrderReviewProps): JSX.Element => {
    const cartRef = useRef(null);

    const {
        cart,
        activeStore,
        orderMode,
        orderNumber,
        send,
        unavailableSku,
        user,
        userAddresses,
        current,
    } = useContext(OrderContext);

    const { checkoutAttemptReference, isNethoneCrashed } = nethone;
    const { items = [], paymentMethod, voucher, homeAddressId, shippingAddressId } = cart;

    const [loading, setIsLoading] = useState(false);
    const [availableProducts, setAvailableProducts] = useState(items);
    const [unavailableProducts, setUnavailableProducts] = useState([]);

    useMount(() => {
        if (current.matches('review')) {
            updateOrderStepState({
                orderMode,
                orderNumber,
                currentStep: STEP_NAMES.ORDER_SUMMARY,
            });
        }
    });

    useEffect(() => {
        setAvailableProducts(items);
    }, [items]);

    const filterByAvailability = useCallback(() => {
        const { available, notAvailable } = items.reduce(
            (memo, item) => {
                if (unavailableSku.find((i) => i.includes(item?.variant?.product?.sku))) {
                    memo.notAvailable.push(item);
                } else {
                    memo.available.push(item);
                }
                return memo;
            },
            { available: [], notAvailable: [] },
        );

        setUnavailableProducts(notAvailable);
        setAvailableProducts(available);
    }, [items, unavailableSku]);

    const dispatchAvailableService = useCallback(
        (action, eventProperty) => {
            registerAvailabilityOfProduct(action, {
                userID: user.id,
                orderID: orderNumber,
                previous_step: 'order_review',
                current_flow: (flowName && flowName.toLowerCase()) || null,
                ...eventProperty,
            });
        },
        [orderNumber, user, flowName],
    );

    useEffect(() => {
        setIsLoading(!checkoutAttemptReference && !isNethoneCrashed);
    }, [checkoutAttemptReference, isNethoneCrashed]);

    useEffect(() => {
        if (orderMode === OrderModes.Flex) {
            if (unavailableSku && Array.isArray(unavailableSku) && !!unavailableSku.length) {
                if (!!cartRef && cartRef.current) {
                    cartRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
                }

                setIsLoading(false);
                filterByAvailability();
                dispatchAvailableService('enter', { productSKU: unavailableSku });
            }
        }
    }, [
        dispatchAvailableService,
        filterByAvailability,
        items,
        orderMode,
        orderNumber,
        send,
        unavailableSku,
        voucher,
    ]);

    const removeAllUnavailableProducts = useCallback(
        () =>
            send('REMOVE_LINE_ITEMS', {
                items: unavailableProducts,
            }),
        [send, unavailableProducts],
    );

    const renderProductCards = useMemo(
        () => (
            <ItemsContainer ref={cartRef}>
                {unavailableProducts.map((i) => (
                    <UnavailableItemMemoized
                        item={i}
                        key={i.id}
                        removeAllUnavailableProducts={removeAllUnavailableProducts}
                    />
                ))}

                {availableProducts.map((i) => {
                    const announcementData =
                        getProductAnnouncementData(
                            i.variant.sku.split('V').shift(),
                            activeStore?.code,
                            announcementFlagData,
                        ) ?? null;

                    return (
                        <div key={i.id}>
                            <CartItem
                                item={i}
                                key={i && i.id}
                                isOrderReview
                                closeCheckout={closeCheckout}
                            />

                            {announcementData && !!announcementData.checkoutText && (
                                <ConsentNote>
                                    <Translator
                                        tk={announcementData.checkoutText}
                                        withValues={{ storeCode: activeStore?.code }}
                                    />
                                </ConsentNote>
                            )}
                        </div>
                    );
                })}
            </ItemsContainer>
        ),
        [
            unavailableProducts,
            availableProducts,
            removeAllUnavailableProducts,
            closeCheckout,
            announcementFlagData,
            activeStore,
        ],
    );

    const handleSubmit = useCallback(() => {
        if (loading) {
            return;
        }

        setIsLoading(true);

        send('SUBMIT_ORDER', {
            meta: { attempt_reference: checkoutAttemptReference },
        });
    }, [send, loading, checkoutAttemptReference]);

    const handleAvailableSubmit = useCallback(() => {
        if (loading) {
            return;
        }

        setIsLoading(true);

        send('SUBMIT_AVAILABLE_ORDER', {
            items: unavailableProducts,
            meta: { attempt_reference: checkoutAttemptReference },
        });

        dispatchAvailableService('exit.confirm', {
            productSKU: unavailableSku,
        });
    }, [
        loading,
        send,
        unavailableProducts,
        checkoutAttemptReference,
        dispatchAvailableService,
        unavailableSku,
    ]);

    const pricesBasedOnAvailableProducts = useMemo(() => {
        return unavailableProducts.reduce(
            (memo, item) => {
                memo.totalCents -= item.priceCents * item.quantity;
                memo.itemTotalCents -= item.priceCents * item.quantity;
                return memo;
            },
            { totalCents: cart.totalPrice, itemTotalCents: cart.itemTotal },
        );
    }, [unavailableProducts, cart.totalPrice, cart.itemTotal]);

    const submitOrderKey = useMemo(
        () =>
            ({
                control: 'NEW_CHECKOUT_SUBMIT_ORDER',
                variant_1: 'NEW_CHECKOUT_SUBMIT_ORDER_VARIANT_1',
                variant_2: 'NEW_CHECKOUT_SUBMIT_ORDER_VARIANT_2',
                variant_3: 'NEW_CHECKOUT_SUBMIT_ORDER_VARIANT_3',
            }[checkoutOrderReviewCTA?.variantKey ?? 'control']),
        [checkoutOrderReviewCTA],
    );

    const renderCartSummary = useMemo(
        () => (
            <>
                <Summary
                    cart={
                        !!unavailableProducts.length
                            ? {
                                  ...cart,
                                  totalPrice: pricesBasedOnAvailableProducts.totalCents,
                                  itemTotal: pricesBasedOnAvailableProducts.itemTotalCents,
                              }
                            : cart
                    }
                    activeStore={activeStore}
                />

                <ButtonWrap>
                    <ButtonV2
                        fullWidth
                        color="red"
                        onClick={unavailableProducts.length ? handleAvailableSubmit : handleSubmit}
                        isDisabled={
                            unavailableProducts.length === items.length ||
                            loading ||
                            (orderMode === 'MIX' && items.length < 3)
                        }
                        data-testid="summary-cta-test-id"
                    >
                        <Translator tk={submitOrderKey} />
                    </ButtonV2>

                    {loading && <CircleLoader />}
                </ButtonWrap>
            </>
        ),
        [
            unavailableProducts.length,
            cart,
            pricesBasedOnAvailableProducts.totalCents,
            pricesBasedOnAvailableProducts.itemTotalCents,
            activeStore,
            handleAvailableSubmit,
            handleSubmit,
            items.length,
            loading,
            orderMode,
            submitOrderKey,
        ],
    );

    const editShippingAddress = useCallback(() => {
        send('EDIT_SHIPPING_ADDRESS');
    }, [send]);

    const editHomeAddress = useCallback(() => {
        send('EDIT_HOME_ADDRESS');
    }, [send]);

    const editPaymentMethod = useCallback(() => {
        send('EDIT_PAYMENT_METHOD');
    }, [send]);

    return (
        <Container data-testid="summary-test-id">
            <TouchScrollable>
                <Header titleOrder="1">
                    <PureTranslator
                        tk={
                            userDevice !== 'mobile'
                                ? stepData.heading
                                : 'NEW_CHECKOUT_SUMMARY_ORDER_SUMMARY'
                        }
                    />
                </Header>

                <WidgetContainer orderMode={orderMode}>
                    {orderMode === 'MIX' && (
                        <MixItemsWrap>
                            <MixItemsContainer items={items} shouldShowLink={false} isOrderReview />
                        </MixItemsWrap>
                    )}

                    <OrderInformation>
                        <If condition={orderMode === 'FLEX' && userDevice !== 'mobile'}>
                            {renderProductCards}
                        </If>

                        <SummaryWrapper>
                            {renderCartSummary}

                            <SummaryNote>
                                <Translator
                                    tk="SUMMARY_NOTE"
                                    withValues={{
                                        storeCode: activeStore?.country_code?.toLowerCase() ?? 'de',
                                    }}
                                />
                            </SummaryNote>
                        </SummaryWrapper>
                    </OrderInformation>

                    <UserInformation>
                        <If condition={orderMode === OrderModes.Flex && userDevice === 'mobile'}>
                            {renderProductCards}
                        </If>

                        {orderMode === OrderModes.Flex && (
                            <Voucher unavailableProducts={unavailableProducts} />
                        )}

                        <If condition={orderMode === OrderModes.Flex && userDevice === 'mobile'}>
                            <MobileImp>{renderCartSummary}</MobileImp>

                            <Header titleOrder="2">
                                <PureTranslator tk="NEW_CHECKOUT_SUMMARY_ORDER_DETAILS" />
                            </Header>
                        </If>

                        <Cards>
                            <Card
                                titleTk="NEW_CHECKOUT_SUMMARY_ADDRESS"
                                type={CARD_TYPE.address}
                                onClick={editShippingAddress}
                                address={findAddressById({
                                    userAddresses,
                                    addressId: shippingAddressId,
                                })}
                            />

                            {flowName === FLOW_NAMES.CHECKOUT_WITH_HOME_ADDRESS && (
                                <Card
                                    titleTk="NEW_CHECKOUT_SUMMARY_HOME_ADDRESS"
                                    type={CARD_TYPE.address}
                                    onClick={editHomeAddress}
                                    address={findAddressById({
                                        userAddresses,
                                        addressId: homeAddressId,
                                    })}
                                />
                            )}

                            <Card
                                titleTk="NEW_CHECKOUT_SUMMARY_PAYMENT"
                                type={CARD_TYPE.payment}
                                payment={paymentMethod}
                                onClick={editPaymentMethod}
                                showWarning
                            />
                        </Cards>
                    </UserInformation>
                </WidgetContainer>
            </TouchScrollable>
        </Container>
    );
};

const enhance = compose(
    withDeviceInfo,
    connect(
        createStructuredSelector({
            announcementFlagData: productAnnouncementsFlagData,
            checkoutOrderReviewCTA: checkoutOrderReviewCTAFlag,
        }),
    ),
);

export default enhance(OrderReview);
